import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/i18n";
import { Loader } from "@/components/common";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PlanningPage from "@/pages/PlanningPage";
import WorkshopPage from "@/pages/WorkshopPage";
import ReceptionPage from "@/pages/ReceptionPage";
import RepairOrderPage from "@/pages/RepairOrderPage";
import MechanicPage from "@/pages/MechanicPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import VehiclesPage from "@/pages/VehiclesPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import QuotesPage from "@/pages/QuotesPage";
import QuoteDetailPage from "@/pages/QuoteDetailPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import InvoicesPage from "@/pages/InvoicesPage";
import PartsPage from "@/pages/PartsPage";
import MessagesPage from "@/pages/MessagesPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import PortalPage from "@/pages/PortalPage";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <Loader label="Vérification de la session…" className="min-h-screen" />;
  if (!user) return <Navigate to="/connexion" replace />;
  return <Outlet />;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={homeFor(user.role)} replace />;
}

const OPS = ["admin", "workshop_manager", "receptionist", "mechanic", "parts_manager"];
const OFFICE = ["admin", "workshop_manager", "receptionist", "accountant"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="/devis/:token" element={<PublicQuotePage />} />
          <Route element={<Protected />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<Home />} />
              <Route path="/tableau-de-bord" element={<RoleRoute roles={["admin", "workshop_manager", "receptionist", "parts_manager", "accountant"]}><DashboardPage /></RoleRoute>} />
              <Route path="/mes-travaux" element={<RoleRoute roles={["mechanic", "admin", "workshop_manager"]}><MechanicPage /></RoleRoute>} />
              <Route path="/planning" element={<RoleRoute roles={["admin", "workshop_manager", "receptionist", "mechanic"]}><PlanningPage /></RoleRoute>} />
              <Route path="/atelier" element={<RoleRoute roles={OPS}><WorkshopPage /></RoleRoute>} />
              <Route path="/atelier/nouveau" element={<RoleRoute roles={["admin", "workshop_manager", "receptionist"]}><ReceptionPage /></RoleRoute>} />
              <Route path="/atelier/:id" element={<RoleRoute roles={OPS}><RepairOrderPage /></RoleRoute>} />
              <Route path="/clients" element={<RoleRoute roles={OFFICE}><ClientsPage /></RoleRoute>} />
              <Route path="/clients/:id" element={<RoleRoute roles={OFFICE}><ClientDetailPage /></RoleRoute>} />
              <Route path="/vehicules" element={<RoleRoute roles={OPS}><VehiclesPage /></RoleRoute>} />
              <Route path="/vehicules/:id" element={<RoleRoute roles={OPS}><VehicleDetailPage /></RoleRoute>} />
              <Route path="/devis" element={<RoleRoute roles={OFFICE}><QuotesPage /></RoleRoute>} />
              <Route path="/devis/detail/:id" element={<RoleRoute roles={OFFICE}><QuoteDetailPage /></RoleRoute>} />
              <Route path="/factures" element={<RoleRoute roles={OFFICE}><InvoicesPage /></RoleRoute>} />
              <Route path="/pieces" element={<RoleRoute roles={[...OPS]}><PartsPage /></RoleRoute>} />
              <Route path="/messages" element={<RoleRoute roles={["admin", "workshop_manager", "receptionist"]}><MessagesPage /></RoleRoute>} />
              <Route path="/rapports" element={<RoleRoute roles={["admin", "workshop_manager", "accountant"]}><ReportsPage /></RoleRoute>} />
              <Route path="/parametres" element={<RoleRoute roles={["admin", "workshop_manager", "receptionist", "parts_manager", "accountant"]}><SettingsPage /></RoleRoute>} />
              <Route path="/espace-client" element={<RoleRoute roles={["client"]}><PortalPage /></RoleRoute>} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
